import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/318366c51ac2aa409f31377c5849a105b4b9197798a3141dc8672b4f80f68ac9.jpeg";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private Florence day tour with chauffeur",
  name: "Private Florence Tour",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Florence", "Lucca", "Pisa", "Tuscany"],
  description:
    "A private, chauffeur-driven day in Florence from Lucca, Pisa or the coast — the Duomo, museums of Florentine art, Ponte Vecchio and the Oltrarno, at your own pace in a Mercedes V-Class.",
  url: "https://yourblacklimo.com/florence-private-tour",
};

const CONTENT = {
  en: {
    seoTitle: "Private Florence Tour | Chauffeur Day Trip from Lucca & Pisa — Your Black Limo",
    seoDesc:
      "A private, chauffeur-driven day in Florence — the Duomo, museums of Florentine art (including the David), Ponte Vecchio and the Oltrarno. Door-to-door from Lucca, Pisa or the coast in a Mercedes V-Class.",
    overline: "Florence Day Tour",
    h1: "Private Florence Tour — The Renaissance, Unhurried",
    intro:
      "The cradle of the Renaissance, seen at your own rhythm. We collect you from your hotel, villa or ship and drive you straight to the heart of Florence — no parking, no queues at the barriers, just the city laid out before you and a chauffeur handling every detail.",
    heroAlt: "Florence skyline at sunset with the Duomo and Arno river",
    blocks: [
      {
        title: "The city on foot",
        body: "Florence rewards those who wander. From the Duomo and Baptistery to the Signoria and the Ponte Vecchio, the historic centre is best explored slowly. Your chauffeur drops you at the edge of the pedestrian zone and is a phone call away whenever you're ready to move on.",
      },
      {
        title: "Beyond the crowds",
        body: "We know the quiet hours and the artisan workshops of the Oltrarno, the leather masters and the marbled-paper makers. Add a long lunch, a rooftop aperitivo or a sunset from Piazzale Michelangelo — the day is entirely yours to shape.",
      },
    ],
    cards: {
      overline: "Highlights",
      title: "What a day in Florence can hold",
      items: [
        { title: "Duomo & Baptistery", body: "Brunelleschi's dome and the golden Gates of Paradise at the city's spiritual heart." },
        { title: "Museums of Florentine art", body: "Botticelli, Leonardo and Michelangelo's David — book skip-the-line entry directly to avoid the longest queues." },
        { title: "Ponte Vecchio & Oltrarno", body: "The goldsmiths' bridge and the artisan quarter across the Arno." },
        { title: "Piazzale Michelangelo", body: "The classic panorama of Florence, magical at golden hour." },
      ],
    },
    note: {
      title: "How we do it",
      body:
        "We drive you door-to-door — about an hour from Lucca or Pisa — and adapt the day as you go. Skip-the-line museum entries and a licensed local guide can be booked directly by the client; the private car and chauffeur are yours for the full day. Fully licensed NCC, professional English-speaking drivers, Mercedes V-Class for up to seven guests.",
    },
    cta: {
      title: "Plan your private day in Florence",
      secondaryLabel: "See our itineraries",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "How long is the drive to Florence?", a: "About an hour from Lucca or Pisa, and around 90 minutes from the port of Livorno — with a comfortable margin built into every schedule." },
        { q: "Can we customise the day?", a: "Completely. Lunch, shopping, a rooftop aperitivo or a sunset viewpoint — the itinerary is shaped entirely around you." },
        { q: "Is the tour private?", a: "Yes. It is fully private in your own Mercedes V-Class with a dedicated chauffeur — never a shared group." },
      ],
    },
  },
  it: {
    seoTitle: "Tour Privato di Firenze | Escursione con Autista da Lucca e Pisa — Your Black Limo",
    seoDesc:
      "Una giornata privata a Firenze con autista — il Duomo, i musei dove ammirare l'arte fiorentina (incluso il David), Ponte Vecchio e l'Oltrarno. Porta a porta da Lucca, Pisa o dalla costa in Mercedes Classe V.",
    overline: "Tour di Firenze",
    h1: "Tour Privato di Firenze — Il Rinascimento, Senza Fretta",
    intro:
      "La culla del Rinascimento, vissuta al tuo ritmo. Ti veniamo a prendere dal tuo hotel, villa o nave e ti portiamo dritto nel cuore di Firenze — niente parcheggio, niente code ai varchi, solo la città davanti a te e un autista che cura ogni dettaglio.",
    heroAlt: "Skyline di Firenze al tramonto con il Duomo e l'Arno",
    blocks: [
      {
        title: "La città a piedi",
        body: "Firenze premia chi passeggia. Dal Duomo e dal Battistero alla Signoria e al Ponte Vecchio, il centro storico si scopre meglio con calma. Il tuo autista ti lascia al limite della zona pedonale ed è a una telefonata di distanza quando vuoi ripartire.",
      },
      {
        title: "Oltre la folla",
        body: "Conosciamo le ore tranquille e le botteghe artigiane dell'Oltrarno, i maestri del cuoio e della carta marmorizzata. Aggiungi un lungo pranzo, un aperitivo su una terrazza o un tramonto dal Piazzale Michelangelo — la giornata è tutta tua da modellare.",
      },
    ],
    cards: {
      overline: "Da non perdere",
      title: "Cosa può contenere una giornata a Firenze",
      items: [
        { title: "Duomo e Battistero", body: "La cupola del Brunelleschi e la Porta del Paradiso nel cuore spirituale della città." },
        { title: "Musei dell'arte fiorentina", body: "Botticelli, Leonardo e il David di Michelangelo — prenota direttamente l'ingresso salta-fila per evitare le code più lunghe." },
        { title: "Ponte Vecchio e Oltrarno", body: "Il ponte degli orafi e il quartiere artigiano oltre l'Arno." },
        { title: "Piazzale Michelangelo", body: "Il panorama classico di Firenze, magico all'ora dorata." },
      ],
    },
    note: {
      title: "Come lavoriamo",
      body:
        "Ti accompagniamo porta a porta — circa un'ora da Lucca o Pisa — adattando la giornata mentre procede. Ingressi salta-fila ai musei e una guida locale abilitata possono essere prenotati direttamente dal cliente; l'auto privata con autista è a tua disposizione per l'intera giornata. NCC regolarmente autorizzato, autisti professionisti che parlano inglese, Mercedes Classe V fino a sette ospiti.",
    },
    cta: {
      title: "Organizza la tua giornata privata a Firenze",
      secondaryLabel: "Vedi gli itinerari",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Quanto dura il tragitto per Firenze?", a: "Circa un'ora da Lucca o Pisa, e circa 90 minuti dal porto di Livorno — con un comodo margine previsto in ogni programma." },
        { q: "Possiamo personalizzare la giornata?", a: "Completamente. Pranzo, shopping, un aperitivo in terrazza o un punto panoramico al tramonto — l'itinerario è modellato interamente su di te." },
        { q: "Il tour è privato?", a: "Sì. È completamente privato, sulla tua Mercedes Classe V con autista dedicato — mai un gruppo condiviso." },
      ],
    },
  },
};

export default function FlorenceGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="florence-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/florence-private-tour", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={HERO}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
